import React, {FC, useMemo} from 'react'
import {GoodsReceipt, Invoice, PurchaseOrder, Requisition} from '../core/_models'
import {DocBlock, DocField, DocFieldGrid} from './ProcurementDocumentSections'
import ProcurementAttachmentsSection from './ProcurementAttachmentsSection'
import {useProcurementBrandingAssets} from './ProcurementDocumentBranding'
import MoneyDisplay from './MoneyDisplay'
import {
  getContextHeaderLabels,
  getEquipmentNameFromLine,
  getItemDisplayNameFromLine,
  getLinkedDefectDisplay,
  getPartNameFromLine,
  getSubcomponentNameFromLine,
  resolveRequisitionViewContext,
  uniqueJoinedValues,
} from '../core/technicalItemContext'
import {buildInvoiceThreeWayMatchAmounts, resolveInvoiceThreeWayMatchLabel} from '../core/invoiceThreeWayMatch'
import {resolveInvoiceMoneyPresentation, resolveInvoiceOriginalCurrency} from '../core/invoiceMoney'
import {resolvePurchaseOrderOriginalCurrency} from '../core/purchaseOrderMoney'

interface InvoiceProcurementDocumentProps {
  invoice: Invoice
  purchaseOrder?: PurchaseOrder | null
  goodsReceipt?: GoodsReceipt | null
  requisition?: Requisition | null
}

const numberValue = (value: any) =>
  typeof value === 'number' && Number.isFinite(value) ? value : Number(value) || 0

const displayValue = (value: any) => {
  const text = String(value ?? '').trim()
  return text ? text : '-'
}

const formatMoney = (currency: string, value: any) =>
  `${currency} ${numberValue(value).toFixed(2)}`

const InvoiceProcurementDocument: FC<InvoiceProcurementDocumentProps> = ({
  invoice,
  purchaseOrder,
  goodsReceipt,
  requisition,
}) => {
  const requisitionAny = (requisition as any) || ((purchaseOrder as any)?.rfq?.requisition as any) || {}
  const reqLines: any[] = Array.isArray(requisitionAny?.lines) ? requisitionAny.lines : []
  const grLines: any[] = Array.isArray(goodsReceipt?.lines) ? (goodsReceipt?.lines as any[]) : []
  const quotationLines: any[] = Array.isArray(purchaseOrder?.quotationLines)
    ? (purchaseOrder?.quotationLines as any[])
    : []
  const {logoSrc: companyLogoUrl, watermarkSrc: companyWatermarkUrl, ready: brandingReady} =
    useProcurementBrandingAssets(
      (purchaseOrder as any)?.companyLogoUrl ||
        (purchaseOrder as any)?.logoUrl ||
        (purchaseOrder as any)?.company?.logoUrl ||
        requisitionAny?.companyLogoUrl ||
        requisitionAny?.logoUrl ||
        requisitionAny?.company?.logoUrl ||
        requisitionAny?.category?.caid?.logoUrl ||
        requisitionAny?.category?.cgaid?.logoUrl,
      (purchaseOrder as any)?.companyWatermarkUrl ||
        (purchaseOrder as any)?.watermarkUrl ||
        (purchaseOrder as any)?.company?.watermarkUrl ||
        requisitionAny?.companyWatermarkUrl ||
        requisitionAny?.watermarkUrl ||
        requisitionAny?.company?.watermarkUrl ||
        requisitionAny?.category?.caid?.watermarkUrl ||
        requisitionAny?.category?.cgaid?.watermarkUrl,
      requisitionAny?.category?.cgaid || (purchaseOrder as any)?.rfq?.requisition?.category?.cgaid
    )

  const money = resolveInvoiceMoneyPresentation(invoice)
  const invoiceCurrency = displayValue(resolveInvoiceOriginalCurrency(invoice)) === '-' ? 'USD' : String(resolveInvoiceOriginalCurrency(invoice))
  const poCurrency = purchaseOrder ? resolvePurchaseOrderOriginalCurrency(purchaseOrder) : invoiceCurrency
  const invoiceTotal = numberValue(invoice.originalTotal ?? invoice.amount)
  const paidAmount = numberValue(invoice.paidAmount)
  const remainingAmount = Math.max(numberValue(invoice.remainingAmount ?? invoiceTotal - paidAmount), 0)
  const invoiceExchangeRate =
    invoice.exchangeRate != null && Number(invoice.exchangeRate) > 0
      ? Number(invoice.exchangeRate)
      : purchaseOrder?.exchangeRate != null && Number(purchaseOrder.exchangeRate) > 0
      ? Number(purchaseOrder.exchangeRate)
      : null
  const documentLines = useMemo(() => {
    if (quotationLines.length > 0) {
      return quotationLines.map((line: any, index: number) => {
        const reqLine =
          reqLines.find((row: any) => Number(row?.id) === Number(line?.requisitionLineId)) || reqLines[index] || null
        const grLine = grLines[index] || null
        const qty = numberValue(line?.assignedQuantity ?? line?.quantity ?? reqLine?.quantity)
        const unitPrice = numberValue(line?.unitPrice ?? reqLine?.estimatedUnitPrice ?? purchaseOrder?.unitPrice)
        const discountPct = numberValue(line?.discountPercent ?? reqLine?.discountPercent)
        const gstPct = numberValue(line?.gstPercent ?? reqLine?.gstPercent)
        const base = qty * unitPrice
        const afterDiscount = base - (base * discountPct) / 100
        const total = afterDiscount + (afterDiscount * gstPct) / 100
        return {
          itemDescription: getItemDisplayNameFromLine(reqLine || line, index),
          equipmentName: getEquipmentNameFromLine(reqLine || line) || '-',
          subcomponentName: getSubcomponentNameFromLine(reqLine || line) || '-',
          partName: getPartNameFromLine(reqLine || line) || '-',
          partNo: displayValue(reqLine?.part?.partNo || reqLine?.partNumber || grLine?.partNumber),
          drawingNo: displayValue(reqLine?.part?.drawNo || reqLine?.drawingNumber),
          qty,
          reqUnit: displayValue(reqLine?.unitOfMeasurement || line?.unit),
          poUnit: displayValue(line?.unit || reqLine?.unitOfMeasurement),
          invoiceQty: grLine?.receivedQty ?? '-',
          unitPrice,
          discountPct,
          gstPct,
          total,
          accountCode: displayValue(line?.accountCode || reqLine?.accountCode || purchaseOrder?.logisticAccountCode),
          subAccountCode: displayValue(
            line?.subaccountCode || reqLine?.subaccountCode || reqLine?.subAccountingCode
          ),
        }
      })
    }

    if (reqLines.length > 0) {
      return reqLines.map((reqLine: any, index: number) => {
        const grLine = grLines[index] || null
        const qty = numberValue(reqLine?.quantity)
        const unitPrice = numberValue(reqLine?.estimatedUnitPrice ?? purchaseOrder?.unitPrice)
        const discountPct = numberValue(reqLine?.discountPercent)
        const gstPct = numberValue(reqLine?.gstPercent)
        const base = qty * unitPrice
        const afterDiscount = base - (base * discountPct) / 100
        const total = afterDiscount + (afterDiscount * gstPct) / 100
        return {
          itemDescription: getItemDisplayNameFromLine(reqLine, index),
          equipmentName: getEquipmentNameFromLine(reqLine) || '-',
          subcomponentName: getSubcomponentNameFromLine(reqLine) || '-',
          partName: getPartNameFromLine(reqLine) || '-',
          partNo: displayValue(reqLine?.part?.partNo || reqLine?.partNumber),
          drawingNo: displayValue(reqLine?.part?.drawNo || reqLine?.drawingNumber),
          qty,
          reqUnit: displayValue(reqLine?.unitOfMeasurement),
          poUnit: displayValue(reqLine?.unitOfMeasurement),
          invoiceQty: grLine?.receivedQty ?? '-',
          unitPrice,
          discountPct,
          gstPct,
          total,
          accountCode: displayValue(reqLine?.accountCode || purchaseOrder?.logisticAccountCode),
          subAccountCode: displayValue(reqLine?.subaccountCode || reqLine?.subAccountingCode),
        }
      })
    }

    return grLines.map((grLine: any, index: number) => {
      const qty = numberValue(grLine?.expectedQty)
      const unitPrice = numberValue(purchaseOrder?.unitPrice)
      return {
        itemDescription: displayValue(grLine?.itemDescription || `Item ${index + 1}`),
        equipmentName: '-',
        subcomponentName: '-',
        partName: '-',
        partNo: displayValue(grLine?.partNumber),
        drawingNo: '-',
        qty,
        reqUnit: '-',
        poUnit: '-',
        invoiceQty: grLine?.receivedQty ?? '-',
        unitPrice,
        discountPct: 0,
        gstPct: 0,
        total: qty * unitPrice,
        accountCode: displayValue(purchaseOrder?.logisticAccountCode),
        subAccountCode: '-',
      }
    })
  }, [quotationLines, reqLines, grLines, purchaseOrder?.unitPrice, purchaseOrder?.logisticAccountCode])

  const categoryName = displayValue(requisitionAny?.category?.name || requisitionAny?.categoryName)
  const context = resolveRequisitionViewContext(
    requisitionAny?.requestFor || (purchaseOrder as any)?.requestFor,
    categoryName
  )
  const isTechnical = context === 'TECHNICAL'
  const {primary: contextPrimaryLabel, secondary: contextSecondaryLabel} = getContextHeaderLabels(context)
  const servicesHeadName = displayValue(
    requisitionAny?.inventoryItemHead?.name || requisitionAny?.departmentName
  )
  const servicesSubHeadName = displayValue(
    requisitionAny?.inventoryItemSubHead?.name || requisitionAny?.subDepartmentName
  )
  const subCategoryName = displayValue(
    requisitionAny?.inventoryItemSubCategory?.name ||
      requisitionAny?.inventoryItemSubCategoryName ||
      requisitionAny?.inventoryItemSubHead?.name ||
      requisitionAny?.subDepartmentName
  )
  const equipmentSummary = uniqueJoinedValues(documentLines.map((line) => line.equipmentName))
  const subcomponentSummary = uniqueJoinedValues(documentLines.map((line) => line.subcomponentName))
  const contextPrimaryValue =
    context === 'TECHNICAL' ? equipmentSummary : context === 'SERVICES' ? servicesHeadName : categoryName
  const contextSecondaryValue =
    context === 'TECHNICAL'
      ? subcomponentSummary
      : context === 'SERVICES'
      ? servicesSubHeadName
      : subCategoryName

  const totalItemPrice = documentLines.reduce(
    (sum, line) => sum + numberValue(line.qty) * numberValue(line.unitPrice),
    0
  )
  const lineDiscountTotal = documentLines.reduce((sum, line) => {
    const base = numberValue(line.qty) * numberValue(line.unitPrice)
    return sum + (base * numberValue(line.discountPct)) / 100
  }, 0)
  const lineGstVatTotal = documentLines.reduce((sum, line) => {
    const base = numberValue(line.qty) * numberValue(line.unitPrice)
    const discounted = base - (base * numberValue(line.discountPct)) / 100
    return sum + (discounted * numberValue(line.gstPct)) / 100
  }, 0)
  const freightCost = numberValue(purchaseOrder?.freightCost)
  const packagingCost = numberValue(purchaseOrder?.packagingCost)
  const truckCost = numberValue(purchaseOrder?.truckCost)
  const bargeCost = numberValue(purchaseOrder?.bargeCost)
  const otherCost = numberValue(purchaseOrder?.otherCost)
  const vatOnLogistics = numberValue(purchaseOrder?.vatOnLogistics)
  const headerDiscount = numberValue(purchaseOrder?.totalDiscount)
  const grandTotal =
    invoiceTotal > 0
      ? invoiceTotal
      : numberValue(
          purchaseOrder?.totalAmount ??
            totalItemPrice +
              lineGstVatTotal +
              freightCost +
              packagingCost +
              truckCost +
              bargeCost +
              otherCost +
              vatOnLogistics -
              headerDiscount
        )

  const rfqRef = displayValue((purchaseOrder as any)?.rfq?.title || purchaseOrder?.rfqNo)
  const requisitionCode = displayValue(requisitionAny?.requisition_code || purchaseOrder?.requisitionNo)
  const requisitionTitle = displayValue(requisitionAny?.name || requisitionAny?.title)
  const linkedDefectDisplay = displayValue(getLinkedDefectDisplay(requisitionAny))
  const vesselName = displayValue(requisitionAny?.vessel?.fleet_name || requisitionAny?.vesselName)
  const deliveryPort = displayValue(
    purchaseOrder?.locationOfDelivery || (purchaseOrder as any)?.rfq?.deliveryPortOverride?.main_port
  )
  const quotationContext = displayValue(
    purchaseOrder?.quotationId
      ? `Q-${purchaseOrder.quotationId} / ${purchaseOrder?.vendor?.name || '-'}`
      : purchaseOrder?.vendor?.name
  )

  const rootClassName = `procurement-doc invoice-doc${companyWatermarkUrl ? ' procurement-has-watermark' : ''}`

  return (
    <div className={rootClassName} data-export-ready={brandingReady ? 'true' : 'false'}>
      {companyWatermarkUrl ? (
        <div className='procurement-doc-watermark' aria-hidden='true'>
          <img
            src={companyWatermarkUrl}
            alt=''
            className='procurement-doc-watermark-image'
            crossOrigin='anonymous'
            data-pdf-watermark-source='true'
          />
        </div>
      ) : null}
      <div className='procurement-doc-content'>
        {companyLogoUrl ? (
          <div className='procurement-doc-brand-header mb-3'>
            <img
              src={companyLogoUrl}
              alt='Company Logo'
              className='procurement-doc-brand-logo'
              crossOrigin='anonymous'
              data-pdf-logo='true'
            />
          </div>
        ) : null}

        <DocBlock title='Invoice Header'>
          <DocFieldGrid columns={4}>
            <DocField label='Invoice Number' value={displayValue(invoice.invoiceNumber)} />
            <DocField label="Vendor's Invoice Number" value={displayValue(invoice.vendorInvoiceNumber)} />
            <DocField label='Invoice Date' value={displayValue(invoice.invoiceDate)} />
            <DocField label='Status' value={displayValue(invoice.status)} />
            <DocField label='Currency' value={invoiceCurrency} />
            <DocField label='Company Exchange Rate' value={invoiceExchangeRate != null ? invoiceExchangeRate.toFixed(4) : '-'} />
            <DocField label='Company Currency' value={displayValue(invoice.companyCurrencyCode || '-')} />
            <DocField label='Locked Rate' value={displayValue(invoice.exchangeRateDisplay || (invoice.exchangeRate != null ? String(invoice.exchangeRate) : '-'))} />
            <DocField
              label='Invoice Amount'
              value={
                <MoneyDisplay
                  compact
                  primaryAmount={money.primaryAmount}
                  primaryCurrency={money.primaryCurrency}
                  secondaryAmount={money.secondaryAmount}
                  secondaryCurrency={money.secondaryCurrency}
                  sameCurrency={money.sameCurrency}
                  exchangeRateDisplay={money.auxiliaryText || money.exchangeRateDisplay}
                />
              }
            />
            <DocField label='Paid Amount' value={formatMoney(invoiceCurrency, paidAmount)} />
            <DocField label='Remaining Amount' value={formatMoney(invoiceCurrency, remainingAmount)} />
            <DocField
              label='PO Aggregate 3-Way Match'
              value={resolveInvoiceThreeWayMatchLabel(invoice)}
            />
            {buildInvoiceThreeWayMatchAmounts(invoice, invoiceCurrency).map((row) => (
              <DocField key={row.label} label={row.label} value={row.value} />
            ))}
            {invoice.threeWayMatchSummary ? (
              <DocField label='Match Detail' value={invoice.threeWayMatchSummary} />
            ) : null}
            <DocField
              label='Vendor'
              value={displayValue(purchaseOrder?.vendor?.name || `V-${invoice.vendorId}`)}
            />
            <DocField
              label='PO Reference'
              value={displayValue(purchaseOrder?.poNumber || `PO-${invoice.purchaseOrderId}`)}
            />
            <DocField
              label='GR Reference'
              value={displayValue(
                goodsReceipt?.id ? `GR-${goodsReceipt.id}` : invoice.goodsReceiptId ? `GR-${invoice.goodsReceiptId}` : '-'
              )}
            />
            <DocField label='PO Currency' value={poCurrency} />
          </DocFieldGrid>
        </DocBlock>

        <DocBlock title='PO Details'>
          <DocFieldGrid columns={4}>
            <DocField label='PO Number' value={displayValue(purchaseOrder?.poNumber || `PO-${invoice.purchaseOrderId}`)} />
            <DocField label='PO Date' value={displayValue(purchaseOrder?.poDate)} />
            <DocField
              label='Company Header'
              value={displayValue(purchaseOrder?.poRaisedByCompany || requisitionAny?.category?.cgaid?.name)}
            />
            <DocField label='Vendor Name' value={displayValue(purchaseOrder?.vendor?.name)} />
            <DocField label='Issued By User' value={displayValue(purchaseOrder?.poRaisedByUser)} />
            <DocField label='Invoice Addressed To' value={displayValue(purchaseOrder?.invoiceAddressedTo)} />
            <DocField label='Supplier Reference' value={displayValue(purchaseOrder?.supplierReference)} />
            <DocField label='Delivery Date' value={displayValue(purchaseOrder?.deliveryDate)} />
            <DocField label='PO Status' value={displayValue(purchaseOrder?.status)} />
            <DocField
              label='Approver'
              value={displayValue(
                purchaseOrder?.secondApproverUsername ||
                  (purchaseOrder as any)?.approver ||
                  (purchaseOrder as any)?.ownerActionByUsername
              )}
            />
            <DocField label='Logistic Account Code' value={displayValue(purchaseOrder?.logisticAccountCode)} />
          </DocFieldGrid>
        </DocBlock>

        <DocBlock title='Requisition / RFQ / Quotation Context'>
          <DocFieldGrid columns={4}>
            <DocField label='Requisition Number' value={requisitionCode} />
            <DocField label='Requisition Title' value={requisitionTitle} />
            <DocField label='Linked Defect' value={linkedDefectDisplay} />
            <DocField label='Vessel' value={vesselName} />
            <DocField label={contextPrimaryLabel} value={contextPrimaryValue} />
            <DocField label={contextSecondaryLabel} value={contextSecondaryValue} />
            <DocField label='Urgency' value={displayValue(requisitionAny?.type)} />
            <DocField label='Delivery Port' value={deliveryPort} />
            <DocField label='RFQ Ref' value={rfqRef} />
            <DocField label='Quotation Context' value={quotationContext} />
          </DocFieldGrid>
        </DocBlock>

        <DocBlock title='Item Details'>
          <div className='table-responsive'>
            <table className='table table-bordered align-middle doc-tight mb-0 procurement-print-table'>
              <thead className='table-light'>
                <tr>
                  <th style={{width: '56px'}}>No</th>
                  <th>Item Description</th>
                  <th style={{width: '82px'}}>Qty</th>
                  <th style={{width: '82px'}}>Req Unit</th>
                  <th style={{width: '82px'}}>PO Unit</th>
                  <th style={{width: '90px'}}>Inv Qty</th>
                  <th style={{width: '120px'}}>Unit Price</th>
                  <th style={{width: '120px'}}>Total</th>
                </tr>
              </thead>
              <tbody>
                {documentLines.length === 0 ? (
                  <tr>
                    <td colSpan={8} className='text-center text-muted py-3'>
                      No line details available.
                    </td>
                  </tr>
                ) : (
                  documentLines.map((line, idx) => (
                    <tr key={`invoice-line-${idx}`} className='procurement-avoid-break'>
                      <td>{idx + 1}</td>
                      <td>
                        <div>{displayValue(line.itemDescription)}</div>
                        {isTechnical ? (
                          <div className='procurement-line-detail'>
                            {contextPrimaryLabel}: {displayValue(line.equipmentName)} | {contextSecondaryLabel}:{' '}
                            {displayValue(line.subcomponentName)} | Part: {displayValue(line.partName)} | Part No:{' '}
                            {displayValue(line.partNo)} | Drawing: {displayValue(line.drawingNo)}
                          </div>
                        ) : (
                          <div className='procurement-line-detail'>
                            {contextPrimaryLabel}: {contextPrimaryValue} | {contextSecondaryLabel}:{' '}
                            {contextSecondaryValue}
                          </div>
                        )}
                        <div className='procurement-line-detail'>
                          Discount: {numberValue(line.discountPct).toFixed(2)}% | GST/VAT:{' '}
                          {numberValue(line.gstPct).toFixed(2)}% | Account: {displayValue(line.accountCode)} | Sub
                          Account: {displayValue(line.subAccountCode)}
                        </div>
                      </td>
                      <td>{numberValue(line.qty)}</td>
                      <td>{displayValue(line.reqUnit)}</td>
                      <td>{displayValue(line.poUnit)}</td>
                      <td>{displayValue(line.invoiceQty)}</td>
                      <td>{formatMoney(invoiceCurrency, line.unitPrice)}</td>
                      <td>{formatMoney(invoiceCurrency, line.total)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </DocBlock>

        <DocBlock title='Commercial Summary'>
          <DocFieldGrid columns={4}>
            <DocField label='Total Item Price' value={formatMoney(invoiceCurrency, totalItemPrice)} />
            <DocField label='Freight / Trucking' value={formatMoney(invoiceCurrency, freightCost)} />
            <DocField label='Pkg & Handling' value={formatMoney(invoiceCurrency, packagingCost)} />
            <DocField label='Truck Cost' value={formatMoney(invoiceCurrency, truckCost)} />
            <DocField label='Barge / Workboat' value={formatMoney(invoiceCurrency, bargeCost)} />
            <DocField label='Other Cost' value={formatMoney(invoiceCurrency, otherCost)} />
            <DocField label='GST/VAT on Logistic' value={formatMoney(invoiceCurrency, vatOnLogistics)} />
            <DocField label='Line Discount Total' value={formatMoney(invoiceCurrency, lineDiscountTotal)} />
            <DocField label='Header Discount' value={formatMoney(invoiceCurrency, headerDiscount)} />
            <DocField label='Line GST/VAT Total' value={formatMoney(invoiceCurrency, lineGstVatTotal)} />
            <DocField label='Grand Total' value={formatMoney(invoiceCurrency, grandTotal)} />
          </DocFieldGrid>
        </DocBlock>

        <ProcurementAttachmentsSection attachments={(invoice as any)?.attachments} />
      </div>
    </div>
  )
}

export default InvoiceProcurementDocument
